import { swSettings } from "app/common/services/swSettings";
import { i18nFilter } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { IndustryAnalysisLoyalty } from "app/pages/industry-analysis/category-loyalty/IndustryAnalysisLoyalty";
import { LOYALTY_CATEGORIES_COLORS } from "app/pages/industry-analysis/category-loyalty/LoyaltyCategoriesColors";
import type { ICategoryLoyaltyTableData } from "app/pages/website-analysis/audience-loyalty/LoyaltyApiService";
import LoyaltyApiService from "app/pages/website-analysis/audience-loyalty/LoyaltyApiService";
import { useEffect, useMemo, useState } from "react";
import categoryService from "app/common/services/categoryService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { useCurrentStateAndParams } from "@uirouter/react";

export const IndustryAnalysisLoyaltyContainer = () => {
    const { params } = useCurrentStateAndParams();
    const [categoryLoyaltyLoading, setCategoryLoyaltyLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const [loyaltyTransformedBarData, setLoyaltyTransformedBarData] = useState<any[]>(null);
    const [tableData, setTableData] = useState<ICategoryLoyaltyTableData>(null);

    const { services } = useMemo(() => {
        return {
            services: {
                swNavigator: proNavigator,
                apiService: new LoyaltyApiService(),
                swSettings,
            },
        };
    }, []);

    const pageFilters = useMemo<any>(() => {
        return services.swNavigator.getApiParams(params);
    }, []);

    const categoryLoyaltyBarChartAdaptor = (datum: any) => {
        return Object.keys(datum).map((bucketName) => {
            return {
                backgroundColor: LOYALTY_CATEGORIES_COLORS[bucketName].backgroundColor,
                color: LOYALTY_CATEGORIES_COLORS[bucketName].color,
                name: i18nFilter()(`industry.analysis.loyalty.legend.${bucketName}`),
                text: percentageSignFilter()(datum?.[bucketName]?.AvgPercentageUsers, 2),
                width: datum?.[bucketName]?.AvgPercentageUsers,
            };
        });
    };

    useEffect(() => {
        const categoryObject = categoryService.categoryQueryParamToCategoryObject(pageFilters.category);
        const loadCategoryChartData = async () => {
            try {
                setCategoryLoyaltyLoading(true);
                const categoryLoyaltyData = await services.apiService.getCategoryLoyalty({
                    ...pageFilters,
                    keys: categoryObject?.forApi,
                    category: categoryObject?.forDisplayApi,
                    categoryHash: categoryObject?.categoryHash,
                });
                const chartData =
                    categoryLoyaltyData && categoryLoyaltyData.Data
                        ? categoryLoyaltyBarChartAdaptor(
                              categoryLoyaltyData.Data?.[pageFilters.category.replace("~", "/")],
                          )
                        : null;
                setLoyaltyTransformedBarData(chartData);
            } finally {
                setCategoryLoyaltyLoading(false);
            }
        };

        const loadTableData = async () => {
            try {
                setTableLoading(true);
                const tableApiData = await services.apiService.getCategoryLoyaltyTable({
                    ...pageFilters,
                    keys: [pageFilters.category],
                });
                setTableData(tableApiData);
            } catch (e) {
                setTableData(null);
            } finally {
                setTableLoading(false);
            }
        };
        const loadTableAndGraphData = async () => {
            try {
                setTableLoading(true);
                setCategoryLoyaltyLoading(true);
                const tableAndGraphData = await services.apiService.getCategoryLoyaltyTable(
                    {
                        ...pageFilters,
                        keys: [pageFilters.category],
                    },
                    "api/category/audienceLoyalty/Data",
                );
                setTableData(tableAndGraphData);

                const chartData = categoryLoyaltyBarChartAdaptor(tableAndGraphData.Total);
                setLoyaltyTransformedBarData(chartData);
            } catch (e) {
                setTableData(null);
            } finally {
                setTableLoading(false);
                setCategoryLoyaltyLoading(false);
            }
        };
        if (categoryObject.isCustomCategory) {
            loadTableAndGraphData();
        } else {
            loadCategoryChartData();
            loadTableData();
        }
    }, []);

    return (
        <IndustryAnalysisLoyalty
            isCategoryChartLoading={categoryLoyaltyLoading}
            categoryChartData={loyaltyTransformedBarData}
            isTableLoading={tableLoading}
            tableData={tableData}
            pageFilters={pageFilters}
        />
    );
};
